import React, { useRef } from 'react';
import { Box, Divider } from '@mui/material';
import {
  FormProps,
  FormHeader,
  ClientNameField,
  DocumentUploadArea,
  FactsDescriptionField,
  PieceToRebutField,
  AudioRecordingSection,
  WritingStyleSelector,
  InfoTip,
  PieceTypeSelector,
} from './shared';
// Import new components
import TopicSelector from './TopicSelector';
import { CONTESTACAO_TOPICS } from './ContestacaoTopics';

// Peças que podem ser rebatidas em uma contestação
const PECAS_REBATER = [
  'Petição Inicial',
  'Emenda à Inicial',
  'Réplica',
  'Impugnação',
  'Manifestação',
  'Outro'
];

const ContestacaoForm: React.FC<FormProps> = ({
  documentType,
  values,
  onChange,
  uploadedFiles,
  onFileUpload,
  onFileRemove,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedStyle, setSelectedStyle] = React.useState(values.estilo_escrita || 'padrao');

  const handleFieldChange = (fieldId: string, value: any) => {
    onChange({
      ...values,
      [fieldId]: value,
    });
  };

  const handleRecordingComplete = (audioBlob: Blob) => {
    const audioFile = new File([audioBlob], `audio_${Date.now()}.webm`, {
      type: 'audio/webm',
    });
    onFileUpload([audioFile]);
  };

  return (
    <Box>
      <FormHeader documentType={documentType} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* 1. Tipo da Peça (Comum ou JEC) */}
        <PieceTypeSelector
          value={values.tipo_peca || 'comum'}
          onChange={(value) => handleFieldChange('tipo_peca', value)}
          options={[
            { value: 'comum', label: 'Comum' },
            { value: 'jec', label: 'JEC', description: 'Juizado Especial Cível' },
          ]}
        />

        {/* 2. Nome do Cliente */}
        <ClientNameField
          value={values.nome_cliente || ''}
          onChange={(value) => handleFieldChange('nome_cliente', value)}
        />

        {/* 3. Upload de Documentos */}
        <DocumentUploadArea
          uploadedFiles={uploadedFiles}
          onFileUpload={onFileUpload}
          onFileRemove={onFileRemove}
          suggestedDocs="Petição inicial, Processo na íntegra e Provas."
          fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
        />

        {/* 4. Descrição dos Fatos (Opcional) */}
        <FactsDescriptionField
          value={values.fatos_caso || ''}
          onChange={(value) => handleFieldChange('fatos_caso', value)}
          placeholder="Descreva a versão do seu cliente sobre os fatos, contradições na petição inicial, etc..."
        />

        {/* 5. Tópicos a Suscitar (Novo Campo) */}
        <TopicSelector
          topics={CONTESTACAO_TOPICS}
          selectedTopics={values.topicos_contestacao || []}
          onChange={(selected) => {
            // Update both the array and a formatted string for better AI prompting
            onChange({
              ...values,
              topicos_contestacao: selected,
              // Format as a bulleted list string for the prompt
              topicos_contestacao_texto: selected.length > 0
                ? "Tópicos a suscitar na contestação:\n- " + selected.join("\n- ")
                : ""
            });
          }}
        />

        {/* 6. Peça que deseja rebater */}
        <PieceToRebutField
          value={values.peca_rebater || 'Petição Inicial'}
          onChange={(value) => handleFieldChange('peca_rebater', value)}
          otherValue={values.peca_rebater_outro || ''}
          onOtherChange={(value) => handleFieldChange('peca_rebater_outro', value)}
          options={PECAS_REBATER}
        />

        {/* Gravação de Áudio */}
        <AudioRecordingSection
          onRecordingComplete={handleRecordingComplete}
        />
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Estilo de Escrita */}
      <WritingStyleSelector
        selectedStyle={selectedStyle}
        onStyleChange={(styleId) => {
          setSelectedStyle(styleId);
          handleFieldChange('estilo_escrita', styleId);
        }}
      />

      <InfoTip message="Anexe a petição inicial para que a IA possa identificar os pontos a serem contestados. Quanto mais completo o processo, melhor será a contestação gerada." />
    </Box>
  );
};

export default ContestacaoForm;

